"use client"

import { useState, useEffect, useRef } from "react"
import { getDatabase, ref, onValue, push, set, remove, update } from "firebase/database"
import { FaPaperPlane, FaBell, FaTimes, FaCopy, FaCheck } from "react-icons/fa"
import "./current-rates.css"
import InfoIcon from "./infoIcon"

const CurrentRates = () => {
  const [rateRequests, setRateRequests] = useState([])
  const [filteredRequests, setFilteredRequests] = useState([])
  const [companyFilter, setCompanyFilter] = useState("All")
  const [companyNames, setCompanyNames] = useState([])
  const [newRates, setNewRates] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [hasNewRequests, setHasNewRequests] = useState(false)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedRequests, setSelectedRequests] = useState(new Set())
  const [previousRates, setPreviousRates] = useState({})
  const [selectAll, setSelectAll] = useState(false)
  const [copiedAddresses, setCopiedAddresses] = useState({})

  const bellRef = useRef(null)
  const listeners = useRef([])
  const seenTracker = useRef(new Set())

  const cleanupListeners = () => {
    listeners.current.forEach((unsubscribe) => unsubscribe())
    listeners.current = []
  }

  useEffect(() => {
    setIsLoading(true)
    setError("")
    const db = getDatabase()
    const rateRequestsRef = ref(db, "rateRequest")

    const unsubscribe = onValue(
      rateRequestsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val()
          const requestsList = []
          const companies = new Set()
          let hasNew = false
          const previousRatesData = {}

          Object.keys(data).forEach((userId) => {
            Object.keys(data[userId]).forEach((requestId) => {
              const request = data[userId][requestId]
              const requestWithIds = {
                id: requestId,
                userId,
                ...request,
                status: request.status || "open",
              }

              requestsList.push(requestWithIds)
              if (request.companyName) companies.add(request.companyName)
              if (!request.rate && !request.seen) {
                hasNew = true
              }

              if (request.previousRate) {
                previousRatesData[requestId] = request.previousRate
              }
            })
          })

          requestsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          setRateRequests(requestsList)
          setCompanyNames(["All", ...Array.from(companies)])
          setHasNewRequests(hasNew)
          setPreviousRates(previousRatesData)
          setIsLoading(false)

          if (bellRef.current) {
            bellRef.current.classList.toggle("flash-bell", hasNew)
          }

          window.dispatchEvent(new CustomEvent("newRateRequests", { detail: { hasNew } }))
        } else {
          setRateRequests([])
          setCompanyNames(["All"])
          setIsLoading(false)
        }
      },
      (error) => {
        console.error("Error fetching rate requests:", error)
        setError("Error fetching rate requests.")
        setIsLoading(false)
      },
    )

    listeners.current.push(unsubscribe)
    return () => cleanupListeners()
  }, [])

  useEffect(() => {
    const filtered =
      companyFilter === "All" ? rateRequests : rateRequests.filter((r) => r.companyName === companyFilter)
    setFilteredRequests(filtered)
  }, [rateRequests, companyFilter])

  // Copy site address to localStorage for use in other components
  const copySiteAddress = (request) => {
    if (!request.siteAddress) {
      alert("No site address available to copy")
      return
    }

    const siteData = {
      address: request.siteAddress.address || "",
      state: request.siteAddress.state || "",
      pincode: request.siteAddress.pincode || "",
      companyName: request.companyName || "",
      brand: request.brand || "",
      timestamp: new Date().toISOString(),
      requestId: request.id,
    }

    // Get existing copied addresses from localStorage
    const existingAddresses = JSON.parse(localStorage.getItem("copiedSiteAddresses") || "[]")

    // Check if this address already exists
    const addressExists = existingAddresses.some(
      (addr) => addr.address === siteData.address && addr.state === siteData.state && addr.pincode === siteData.pincode,
    )

    if (!addressExists) {
      // Add new address to the beginning of the array
      const updatedAddresses = [siteData, ...existingAddresses.slice(0, 19)] // Keep only last 20 addresses
      localStorage.setItem("copiedSiteAddresses", JSON.stringify(updatedAddresses))
    }

    // Show visual feedback
    setCopiedAddresses((prev) => ({ ...prev, [request.id]: true }))
    setTimeout(() => {
      setCopiedAddresses((prev) => ({ ...prev, [request.id]: false }))
    }, 2000)

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent("siteAddressCopied", {
        detail: { siteData, message: "Site address copied successfully!" },
      }),
    )
  }

  const handleRateChange = (requestId, value) => {
    setNewRates((prev) => ({ ...prev, [requestId]: value }))
  }

  const markAsSeen = async (request) => {
    const uniqueKey = `${request.userId}_${request.id}`
    if (seenTracker.current.has(uniqueKey)) return

    seenTracker.current.add(uniqueKey)

    try {
      const db = getDatabase()
      const requestRef = ref(db, `rateRequest/${request.userId}/${request.id}`)
      await update(requestRef, { seen: true })
    } catch (error) {
      console.error("Error marking request as seen:", error)
    }
  }

  const sendRateResponse = async (request) => {
    const rateValue = Number.parseFloat(newRates[request.id])
    if (!rateValue || isNaN(rateValue)) return alert("Please enter a valid rate")

    try {
      const db = getDatabase()
      const requestRef = ref(db, `rateRequest/${request.userId}/${request.id}`)
      const previousRate = request.rate
        ? {
            value: request.rate,
            respondedBy: request.respondedBy || "Unknown",
          }
        : null

      await update(requestRef, {
        rate: rateValue,
        respondedBy: localStorage.getItem("adminEmail") || "Admin",
        seen: true,
        previousRate,
      })

      const notificationRef = push(ref(db, `user_notifications/Rate Response/${request.userId}`))
      await set(notificationRef, {
        title: "Rate Response",
        message: `Your rate request for ${request.brand || "Brand"} (${request.grade || ""}), located at ${request.siteAddress.address || ""}, ${request.siteAddress.state || ""} - ${request.siteAddress.pincode || ""}, has been responded to with a rate of ₹${rateValue} per unit.`,
        timestamp: new Date().toISOString(),
        seen: false,
        gifUrl: "/Images/New notification.gif",
      })

      setNewRates((prev) => ({ ...prev, [request.id]: "" }))
      alert("Rate response sent successfully!")
    } catch (error) {
      console.error("Error sending rate response:", error)
      alert("Failed to send rate response.")
    }
  }

  const updateRate = async (request) => {
    const rateValue = Number.parseFloat(newRates[request.id])
    if (!rateValue || isNaN(rateValue)) return alert("Please enter a valid rate")

    try {
      const db = getDatabase()
      const requestRef = ref(db, `rateRequest/${request.userId}/${request.id}`)
      const previousRate = {
        value: request.rate,
        respondedBy: request.respondedBy || "Unknown",
      }

      await update(requestRef, {
        rate: rateValue,
        respondedBy: localStorage.getItem("adminEmail") || "Admin",
        previousRate,
      })

      const notificationRef = push(ref(db, `user_notifications/Rate Response/${request.userId}`))
      await set(notificationRef, {
        title: "Rate Updated",
        message: `The rate for ${request.brand || "Brand"} (${request.grade || ""}), located at ${request.siteAddress.address || ""}, ${request.siteAddress.state || ""} - ${request.siteAddress.pincode || ""}, has been updated to ₹${rateValue} per unit.`,
        timestamp: new Date().toISOString(),
        seen: false,
        gifUrl: "/Images/New notification.gif",
      })

      setNewRates((prev) => ({ ...prev, [request.id]: "" }))
      alert("Rate updated successfully!")
    } catch (error) {
      console.error("Error updating rate:", error)
      alert("Failed to update rate.")
    }
  }

  const closeRequest = async (request) => {
    if (!window.confirm("Are you sure you want to close this rate request?")) return

    try {
      const db = getDatabase()
      const requestRef = ref(db, `rateRequest/${request.userId}/${request.id}`)
      await update(requestRef, { status: "close" })
      alert("Rate request closed successfully!")
    } catch (error) {
      console.error("Error closing rate request:", error)
      alert("Failed to close rate request.")
    }
  }

  const toggleSelectMode = () => {
    setSelectMode(!selectMode)
    setSelectedRequests(new Set())
    setSelectAll(false)
  }

  const toggleSelectRequest = (requestId) => {
    setSelectedRequests((prevSelected) => {
      const newSelected = new Set(prevSelected)
      if (newSelected.has(requestId)) {
        newSelected.delete(requestId)
      } else {
        newSelected.add(requestId)
      }
      return newSelected
    })
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRequests(new Set())
    } else {
      const allIds = new Set(filteredRequests.map((req) => req.id))
      setSelectedRequests(allIds)
    }
    setSelectAll(!selectAll)
  }

  const deleteSelectedRequests = async () => {
    if (selectedRequests.size === 0) return

    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedRequests.size} selected requests?`)
    if (!confirmDelete) return

    try {
      const db = getDatabase()
      const requestsToDelete = filteredRequests.filter((req) => selectedRequests.has(req.id))

      for (const req of requestsToDelete) {
        const requestRef = ref(db, `rateRequest/${req.userId}/${req.id}`)
        await remove(requestRef)
      }

      alert("Selected requests deleted successfully!")
      setSelectedRequests(new Set())
      setSelectMode(false)
      setSelectAll(false)
    } catch (error) {
      console.error("Error deleting selected requests:", error)
      alert("Failed to delete selected requests.")
    }
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading rate requests...</p>
      </div>
    )
  }

  if (error) return <div className="error-message">{error}</div>

  return (
    <div className="current-rates-container">
      <div className="rates-header">
        <h1>Current Rate Requests <InfoIcon text="The Current Rates Requests page shows all material rate inquiries with details like Date, Company, Brand, Grade, Quantity, and Site Address. Status indicates whether the request is responded to, and User Offer, Previous Rate, and Current Rate provide pricing information. The Actions section allows users to respond, update rates, or track requests, ensuring efficient and transparent material procurement." /> </h1>
        <div className="bell-icon-container" ref={bellRef}>
          <FaBell className={`bell-icon ${hasNewRequests ? "has-new" : ""}`} />
          {hasNewRequests && <span className="notification-badge"></span>}
        </div>
      </div>

      <div className="filter-controls">
        <div className="filter-container">
          <label>Filter by Company:</label>
          <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
            {companyNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="bin-controls">
          <button onClick={toggleSelectMode} className="common-bin-btn">
            {selectMode ? "Cancel Selection" : "🗑️"}
          </button>
          {selectMode && (
            <>
              <button onClick={handleSelectAll} className="select-all-btn">
                {selectAll ? "Deselect All" : "Select All"}
              </button>
              <button onClick={deleteSelectedRequests} className="delete-selected-btn">
                Delete Selected ({selectedRequests.size})
              </button>
            </>
          )}
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="no-requests">
          <p>No rate requests found.</p>
        </div>
      ) : (
        <div className="rates-table-container">
          <table className="rates-table">
            <thead>
              <tr>
                {selectMode && (
                  <th>
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                  </th>
                )}
                <th>Date</th>
                <th>Company</th>
                <th>Brand</th>
                <th>Grade</th>
                <th>Quantity</th>
                <th>Site Address</th>
                <th>Status</th>
                <th>User Offer</th>
                <th>Previous Rate</th>
                <th>Current Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => {
                const isNew = !request.rate && !request.seen
                const isClosed = request.status === "close"
                const isSelected = selectedRequests.has(request.id)
                const prevRate = previousRates[request.id] || request.previousRate
                const isAddressCopied = copiedAddresses[request.id]

                if (isNew) markAsSeen(request)

                return (
                  <tr key={request.id} className={isNew ? "new-request" : ""}>
                    {selectMode && (
                      <td>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelectRequest(request.id)} />
                      </td>
                    )}
                    <td>
                      {(() => {
                        const d = request.timestamp.slice(0, 16).replace("T", " ")
                        const [date, time] = d.split(" ")
                        const [hour, minute] = time.split(":")
                        const h = hour % 12 || 12
                        const ampm = hour >= 12 ? "PM" : "AM"
                        return `${date.split("-").reverse().join("/")} at ${h}:${minute} ${ampm}`
                      })()}
                    </td>
                    <td>{request.companyName}</td>
                    <td>{request.brand || "N/A"}</td>
                    <td>{request.grade || "N/A"}</td>
                    <td>{request.quantity || "N/A"}</td>
                    <td>
                      <div className="site-address-cell">
                        <div className="address-text">
                          {request.siteAddress
                            ? `${request.siteAddress.address || ""}, ${request.siteAddress.state || ""}, ${request.siteAddress.pincode || ""}`
                            : "N/A"}
                        </div>
                        {request.siteAddress && (
                          <button
                            className={`copy-address-btn ${isAddressCopied ? "copied" : ""}`}
                            onClick={() => copySiteAddress(request)}
                            title="Copy site address for new code creation"
                          >
                            {isAddressCopied ? <FaCheck /> : <FaCopy />}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${isClosed ? "closed" : request.rate ? "responded" : "pending"}`}>
                        {isClosed ? "Closed" : request.rate ? "Responded" : "Pending"}
                      </span>
                    </td>
                    <td>{request.userOffer ? `₹${request.userOffer}/unit` : "N/A"}</td>
                    <td>
                      {prevRate ? (
                        <div className="previous-rate">
                          <div>₹{prevRate.value}/unit</div>
                          <div className="rate-timestamp">{new Date(prevRate.timestamp).toLocaleDateString()}</div>
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder={request.rate ? `Current: ₹${request.rate}` : "Enter rate"}
                        value={newRates[request.id] || ""}
                        onChange={(e) => handleRateChange(request.id, e.target.value)}
                        onWheel={(e)=>{e.target.blur();}}
                      />
                    </td>
                    <td className="action-buttons">
                      {!request.rate && !isClosed ? (
                        <button onClick={() => sendRateResponse(request)} title="Send Rate">
                          <FaPaperPlane />
                        </button>
                      ) : !isClosed ? (
                        <button onClick={() => updateRate(request)} title="Update Rate">
                          <FaPaperPlane />
                        </button>
                      ) : null}
                      {!isClosed && (
                        <button onClick={() => closeRequest(request)} title="Close Request">
                          <FaTimes />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default CurrentRates
